import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import practiceService from '../../../../services/practiceService';
import './BpEnrollmentSettingsModal.css';

const DEFAULT_DAILY = [
  { name: 'Wake-up Time', time: '06:00' },
  { name: 'Breakfast Time', time: '08:00' },
  { name: 'Lunch Time', time: '12:00' },
  { name: 'Dinner Time', time: '18:00' },
  { name: 'Bed Time', time: '22:00' }
];

const DEFAULT_BP = [
  { name: 'Wake-up', targetName: 'Wake-up Target Range', start: '05:00', end: '07:59', sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 },
  { name: 'Morning', targetName: 'Morning Target Range', start: '08:00', end: '10:59', sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 },
  { name: 'Noon', targetName: 'Noon Target Range', start: '11:00', end: '13:59', sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 },
  { name: 'Afternoon', targetName: 'Afternoon Target Range', start: '14:00', end: '16:59', sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 },
  { name: 'Evening', targetName: 'Evening Target Range', start: '17:00', end: '04:59', sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90, pulseMin: 60, pulseMax: 100 }
];

const DEFAULT_BG = [
  { name: 'Wake-up Time', targetName: 'Wake-up Target Rangeup Time', start: '06:00', end: '06:59', min: 60, max: 110 },
  { name: 'Before-Breakfast', targetName: 'Before-Breakfast Target Range', start: '07:00', end: '07:59', min: 60, max: 110 },
  { name: 'After-Breakfast', targetName: 'After-Breakfast Target Range', start: '08:00', end: '10:59', min: 70, max: 140 },
  { name: 'Before-Lunch', targetName: 'Before-Lunch Target Range', start: '11:00', end: '12:59', min: 60, max: 110 },
  { name: 'After-Lunch', targetName: 'After-Lunch Target Range', start: '13:00', end: '16:59', min: 70, max: 140 },
  { name: 'Before-Dinner', targetName: 'Before-Dinner Target Range', start: '17:00', end: '18:59', min: 60, max: 110 },
  { name: 'After-Dinner', targetName: 'After-Dinner Target Range', start: '19:00', end: '20:29', min: 70, max: 140 },
  { name: 'Bedtime', targetName: 'Bedtime Target Range', start: '20:30', end: '21:59', min: 60, max: 110 },
  { name: 'Midnight', targetName: 'Midnight Target Range', start: '22:00', end: '05:59', min: 60, max: 110 }
];

const DEFAULT_WEIGHT_PERIODS = [
  { name: 'Morning', start: '06:00', end: '11:59' },
  { name: 'Afternoon', start: '12:00', end: '17:59' },
  { name: 'Evening', start: '18:00', end: '05:59' }
];

const DEFAULT_WEIGHT_TARGETS = {
  weightMin: 66,
  weightMax: 220,
  bodyFatMin: 14,
  bodyFatMax: 27,
  bmiUnderweight: '',
  bmiNormal: 18.5,
  bmiOverweight: 25,
  bmiObese: 30
};

export const monitoringVitalKind = (type) => {
  if (type === 'Blood Pressure') return 'bp';
  if (type === 'Blood Glucose') return 'bg';
  if (type === 'Weight') return 'weight';
  return null;
};

const VITAL_NOUN = {
  bp: 'blood pressure',
  bg: 'blood glucose',
  weight: 'weight'
};

const normalizeName = (value) => String(value ?? '').trim().toLowerCase();

const to24Hour = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const ampm = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    let hour = parseInt(ampm[1], 10);
    const minutes = ampm[2];
    const period = ampm[3].toUpperCase();
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return `${String(hour).padStart(2, '0')}:${minutes}`;
  }
  const plain = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!plain) return '';
  return `${String(parseInt(plain[1], 10)).padStart(2, '0')}:${plain[2]}`;
};

const to12Hour = (value) => {
  const time = to24Hour(value);
  if (!time) return '';
  const [hourStr, minutes] = time.split(':');
  let hour = parseInt(hourStr, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  if (hour > 12) hour -= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minutes} ${period}`;
};

const asNumber = (value, fallback) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const cloneRows = (rows) => rows.map((row) => ({ ...row }));

const mergePracticeRows = (data) => {
  const dailyByName = new Map();
  (data?.dailySchedule || []).forEach((row) => {
    const key = normalizeName(row?.name);
    if (key) dailyByName.set(key, row);
  });
  const periodByName = new Map();
  (data?.bloodPressureTimePeriod || []).forEach((row) => {
    const key = normalizeName(row?.name);
    if (key) periodByName.set(key, row);
  });
  const targetByName = new Map();
  (data?.bloodPressureTargetRange || []).forEach((row) => {
    const key = normalizeName(row?.name);
    if (key) targetByName.set(key, row);
  });

  const daily = DEFAULT_DAILY.map((item) => {
    const db = dailyByName.get(normalizeName(item.name));
    return {
      id: db?.id ?? null,
      name: item.name,
      time: db?.time ? to24Hour(db.time) : item.time
    };
  });

  const periods = DEFAULT_BP.map((item) => {
    const period = periodByName.get(normalizeName(item.name));
    const target = targetByName.get(normalizeName(item.targetName));
    return {
      id: period?.id ?? null,
      targetId: target?.id ?? null,
      name: item.name,
      targetName: item.targetName,
      start: item.start,
      end: period?.endTime ? to24Hour(period.endTime) : item.end,
      sysMin: asNumber(target?.systolicMin, item.sysMin),
      sysMax: asNumber(target?.systolicMax, item.sysMax),
      diaMin: asNumber(target?.diastolicMin, item.diaMin),
      diaMax: asNumber(target?.diastolicMax, item.diaMax),
      pulseMin: asNumber(target?.pulseMin, item.pulseMin),
      pulseMax: asNumber(target?.pulseMax, item.pulseMax)
    };
  });

  return { daily, periods, weightTargets: { ...DEFAULT_WEIGHT_TARGETS } };
};

const rowsByName = (rows) => {
  const map = new Map();
  (rows || []).forEach((row) => {
    const key = normalizeName(row?.name);
    if (key) map.set(key, row);
  });
  return map;
};

const mergeBgRows = (data) => {
  const periodsByName = rowsByName(data?.bloodGlucoseTimePeriod);
  const targetsByName = rowsByName(data?.bloodGlucoseTargetRange);
  const periods = DEFAULT_BG.map((item) => {
    const period = periodsByName.get(normalizeName(item.name));
    const target = targetsByName.get(normalizeName(item.targetName));
    return {
      id: period?.id ?? null,
      targetId: target?.id ?? null,
      name: item.name,
      targetName: item.targetName,
      start: item.start,
      end: period?.endTime ? to24Hour(period.endTime) : item.end,
      min: asNumber(target?.minimum ?? target?.min, item.min),
      max: asNumber(target?.maximum ?? target?.max, item.max)
    };
  });
  return { daily: [], periods, weightTargets: { ...DEFAULT_WEIGHT_TARGETS } };
};

const mergeWeightRows = (data) => {
  const periodsByName = rowsByName(data?.weightTimePeriod);
  const target = data?.weightTargetRange && typeof data.weightTargetRange === 'object'
    ? data.weightTargetRange
    : {};
  const periods = DEFAULT_WEIGHT_PERIODS.map((item) => {
    const period = periodsByName.get(normalizeName(item.name));
    return {
      id: period?.id ?? null,
      name: item.name,
      start: item.start,
      end: period?.endTime ? to24Hour(period.endTime) : item.end
    };
  });
  const weightTargets = {
    ...DEFAULT_WEIGHT_TARGETS,
    weightMin: asNumber(target.weightMin, DEFAULT_WEIGHT_TARGETS.weightMin),
    weightMax: asNumber(target.weightMax, DEFAULT_WEIGHT_TARGETS.weightMax),
    bodyFatMin: asNumber(target.bodyFatMin, DEFAULT_WEIGHT_TARGETS.bodyFatMin),
    bodyFatMax: asNumber(target.bodyFatMax, DEFAULT_WEIGHT_TARGETS.bodyFatMax),
    bmiUnderweight: target.bmiUnderweight ?? '',
    bmiNormal: asNumber(target.bmiNormal, DEFAULT_WEIGHT_TARGETS.bmiNormal),
    bmiOverweight: asNumber(target.bmiOverweight, DEFAULT_WEIGHT_TARGETS.bmiOverweight),
    bmiObese: asNumber(target.bmiObese, DEFAULT_WEIGHT_TARGETS.bmiObese)
  };
  return { daily: [], periods, weightTargets };
};

const sameSettingValue = (left, right) => {
  const leftEmpty = left === '' || left == null;
  const rightEmpty = right === '' || right == null;
  if (leftEmpty || rightEmpty) return leftEmpty && rightEmpty;
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) return leftNumber === rightNumber;
  return String(left) === String(right);
};

const customMatchesPractice = (kind, baseline, periods, weightTargets) => {
  const baseByName = rowsByName(baseline?.periods);
  const fields = kind === 'bg'
    ? ['min', 'max']
    : kind === 'weight'
      ? []
      : ['sysMin', 'sysMax', 'diaMin', 'diaMax', 'pulseMin', 'pulseMax'];
  const periodsMatch = (periods || []).every((row) => {
    const base = baseByName.get(normalizeName(row?.name));
    if (!base) return false;
    if (to24Hour(row.end) !== to24Hour(base.end)) return false;
    return fields.every((field) => sameSettingValue(row[field], base[field]));
  }) && (periods || []).length === (baseline?.periods || []).length;
  if (!periodsMatch) return false;
  if (kind !== 'weight') return true;
  return ['weightMin', 'weightMax', 'bodyFatMin', 'bodyFatMax'].every((field) => (
    sameSettingValue(weightTargets?.[field], baseline?.weightTargets?.[field])
  ));
};

const mergeForKind = (kind, data) => {
  if (kind === 'bg') return mergeBgRows(data);
  if (kind === 'weight') return mergeWeightRows(data);
  return mergePracticeRows(data);
};

const buildCustomPayload = (kind, baseline, current) => {
  const payload = {};
  const daily = current.daily
    .filter((row) => {
      const prev = baseline.daily.find((item) => item.name === row.name);
      return (prev?.time || '') !== (row.time || '');
    })
    .map((row) => ({
      id: row.id,
      name: row.name,
      time: row.time,
      _previous: { time: baseline.daily.find((item) => item.name === row.name)?.time ?? null }
    }));
  if (daily.length) payload.dailySchedule = daily;

  const periods = current.periods
    .filter((row) => {
      const prev = baseline.periods.find((item) => item.name === row.name);
      return (prev?.end || '') !== (row.end || '');
    })
    .map((row) => ({
      id: row.id,
      name: row.name,
      startTime: row.start,
      endTime: row.end,
      _previous: { endTime: baseline.periods.find((item) => item.name === row.name)?.end ?? null }
    }));
  if (periods.length) payload.bloodPressureTimePeriod = periods;

  const targets = current.periods
    .filter((row) => {
      const prev = baseline.periods.find((item) => item.name === row.name);
      if (!prev) return true;
      return (
        prev.sysMin !== row.sysMin ||
        prev.sysMax !== row.sysMax ||
        prev.diaMin !== row.diaMin ||
        prev.diaMax !== row.diaMax ||
        prev.pulseMin !== row.pulseMin ||
        prev.pulseMax !== row.pulseMax
      );
    })
    .map((row) => {
      const prev = baseline.periods.find((item) => item.name === row.name);
      return {
        id: row.targetId,
        name: row.targetName,
        systolicMin: row.sysMin,
        systolicMax: row.sysMax,
        diastolicMin: row.diaMin,
        diastolicMax: row.diaMax,
        pulseMin: row.pulseMin,
        pulseMax: row.pulseMax,
        _previous: prev
          ? {
              systolicMin: prev.sysMin,
              systolicMax: prev.sysMax,
              diastolicMin: prev.diaMin,
              diastolicMax: prev.diaMax,
              pulseMin: prev.pulseMin,
              pulseMax: prev.pulseMax
            }
          : null
      };
    });
  if (targets.length) payload.bloodPressureTargetRange = targets;

  if (kind === 'bg') {
    const bgPayload = {};
    if (periods.length) bgPayload.bloodGlucoseTimePeriod = periods;
    const glucose = current.periods
      .filter((row) => {
        const prev = baseline.periods.find((item) => item.name === row.name);
        if (!prev) return true;
        return prev.min !== row.min || prev.max !== row.max;
      })
      .map((row) => {
        const prev = baseline.periods.find((item) => item.name === row.name);
        return {
          id: row.targetId,
          name: row.targetName,
          minimum: row.min,
          maximum: row.max,
          _previous: prev ? { minimum: prev.min, maximum: prev.max } : null
        };
      });
    if (glucose.length) bgPayload.bloodGlucoseTargetRange = glucose;
    return Object.keys(bgPayload).length ? bgPayload : null;
  }

  if (kind === 'weight') {
    const weightPayload = {};
    if (periods.length) weightPayload.weightTimePeriod = periods;
    const prev = baseline.weightTargets;
    const next = current.weightTargets;
    const changed = prev.weightMin !== next.weightMin
      || prev.weightMax !== next.weightMax
      || prev.bodyFatMin !== next.bodyFatMin
      || prev.bodyFatMax !== next.bodyFatMax;
    if (changed) {
      weightPayload.weightTargetRange = {
        weightMin: next.weightMin,
        weightMax: next.weightMax,
        bodyFatMin: next.bodyFatMin,
        bodyFatMax: next.bodyFatMax,
        bmiUnderweight: prev.bmiUnderweight || '',
        bmiNormal: prev.bmiNormal,
        bmiOverweight: prev.bmiOverweight,
        bmiObese: prev.bmiObese,
        _previous: {
          weightMin: prev.weightMin,
          weightMax: prev.weightMax,
          bodyFatMin: prev.bodyFatMin,
          bodyFatMax: prev.bodyFatMax,
          bmiUnderweight: prev.bmiUnderweight || '',
          bmiNormal: prev.bmiNormal,
          bmiOverweight: prev.bmiOverweight,
          bmiObese: prev.bmiObese
        }
      };
    }
    return Object.keys(weightPayload).length ? weightPayload : null;
  }

  return Object.keys(payload).length ? payload : null;
};

const rangeError = (kind, row) => {
  const pairs = kind === 'bg'
    ? [['min', 'max', 'glucose']]
    : [
        ['sysMin', 'sysMax', 'systolic'],
        ['diaMin', 'diaMax', 'diastolic'],
        ['pulseMin', 'pulseMax', 'pulse']
      ];
  for (const [minKey, maxKey, label] of pairs) {
    if (!Number.isFinite(row[minKey]) || !Number.isFinite(row[maxKey])) {
      return `${row.name} ${label} needs both a minimum and a maximum.`;
    }
    if (row[minKey] > row[maxKey]) {
      return `${row.name} ${label} minimum must be less than or equal to the maximum.`;
    }
  }
  if (!row.end) return `${row.name} needs an end time.`;
  return '';
};

const weightTargetError = (targets) => {
  const pairs = [
    ['weightMin', 'weightMax', 'Weight'],
    ['bodyFatMin', 'bodyFatMax', 'Body fat']
  ];
  for (const [minKey, maxKey, label] of pairs) {
    if (!Number.isFinite(targets?.[minKey]) || !Number.isFinite(targets?.[maxKey])) {
      return `${label} needs both a minimum and a maximum.`;
    }
    if (targets[minKey] > targets[maxKey]) {
      return `${label} minimum must be less than or equal to the maximum.`;
    }
  }
  return '';
};

function AmPmTimeField({ value, onChange, disabled, ariaLabel }) {
  const pickerRef = useRef(null);
  const openPicker = () => {
    if (disabled) return;
    const picker = pickerRef.current;
    if (!picker) return;
    if (typeof picker.showPicker === 'function') {
      try {
        picker.showPicker();
        return;
      } catch {
        /* fall through to focus */
      }
    }
    picker.focus();
    picker.click();
  };

  return (
    <div className={`bp-ampm-time${disabled ? ' is-disabled' : ''}`}>
      <input
        type="text"
        readOnly
        value={to12Hour(value) || ''}
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={openPicker}
      />
      <input
        ref={pickerRef}
        type="time"
        value={to24Hour(value) || ''}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

const BpEnrollmentSettingsModal = ({ open, practiceId, kind = 'bp', onCancel, onConfirm }) => {
  const dialogRef = useRef(null);
  const vitalKind = kind === 'bg' || kind === 'weight' ? kind : 'bp';
  const vitalNoun = VITAL_NOUN[vitalKind];
  const [loading, setLoading] = useState(true);
  const [editingCustom, setEditingCustom] = useState(false);
  const [confirmCustomOpen, setConfirmCustomOpen] = useState(false);
  const [unchangedPromptOpen, setUnchangedPromptOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [baseline, setBaseline] = useState(() => mergeForKind(vitalKind, null));
  const [daily, setDaily] = useState(() => cloneRows(mergeForKind(vitalKind, null).daily));
  const [periods, setPeriods] = useState(() => cloneRows(mergeForKind(vitalKind, null).periods));
  const [weightTargets, setWeightTargets] = useState(() => ({ ...DEFAULT_WEIGHT_TARGETS }));

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setEditingCustom(false);
    setConfirmCustomOpen(false);
    setUnchangedPromptOpen(false);
    setFormError('');
    setLoading(true);

    const load = async () => {
      try {
        const response = practiceId
          ? await practiceService.getScheduleAndTargets(practiceId, { skipCache: true })
          : null;
        const data = response?.data?.success ? response.data.data : null;
        const merged = mergeForKind(vitalKind, data);
        if (cancelled) return;
        setBaseline(merged);
        setDaily(cloneRows(merged.daily));
        setPeriods(cloneRows(merged.periods));
        setWeightTargets({ ...merged.weightTargets });
      } catch (error) {
        console.error('Failed to load practice blood pressure settings:', error);
        if (cancelled) return;
        const merged = mergeForKind(vitalKind, null);
        setBaseline(merged);
        setDaily(cloneRows(merged.daily));
        setPeriods(cloneRows(merged.periods));
        setWeightTargets({ ...merged.weightTargets });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, practiceId, vitalKind]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape' && event.keyCode !== 27) return;
      event.preventDefault();
      event.stopPropagation();
      if (confirmCustomOpen || unchangedPromptOpen) {
        setConfirmCustomOpen(false);
        setUnchangedPromptOpen(false);
        return;
      }
      onCancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onCancel, confirmCustomOpen, unchangedPromptOpen]);

  const editable = editingCustom;
  const previewPeriods = useMemo(
    () => (editable ? periods : baseline.periods),
    [editable, periods, baseline]
  );
  const previewWeight = useMemo(
    () => (editable ? weightTargets : baseline.weightTargets),
    [editable, weightTargets, baseline]
  );

  if (!open) return null;

  const updatePeriod = (index, field, value) => {
    setPeriods((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      if (field === 'end') return { ...row, end: value };
      const parsed = value === '' ? '' : Number(value);
      return { ...row, [field]: parsed === '' || Number.isFinite(parsed) ? parsed : row[field] };
    }));
  };

  const updateWeight = (field, value) => {
    const parsed = value === '' ? '' : Number(value);
    setWeightTargets((prev) => ({
      ...prev,
      [field]: parsed === '' || Number.isFinite(parsed) ? parsed : prev[field]
    }));
  };

  const confirmPractice = () => {
    setEditingCustom(false);
    setFormError('');
    setDaily(cloneRows(baseline.daily));
    setPeriods(cloneRows(baseline.periods));
    setWeightTargets({ ...baseline.weightTargets });
    onConfirm({ mode: 'practice', payload: null });
  };

  const viewPracticeSettings = () => {
    setFormError('');
    setConfirmCustomOpen(false);
    setUnchangedPromptOpen(false);
    setEditingCustom(false);
  };

  const startCustomEdit = () => {
    setFormError('');
    setEditingCustom(true);
  };

  const handleContinue = () => {
    if (vitalKind === 'weight') {
      const periodError = periods.find((row) => !row.end);
      const targetError = weightTargetError(weightTargets);
      const invalid = (periodError ? `${periodError.name} needs an end time.` : '') || targetError;
      if (invalid) {
        setFormError(invalid);
        return;
      }
    } else {
      const invalid = periods.map((row) => rangeError(vitalKind, row)).find(Boolean);
      if (invalid) {
        setFormError(invalid);
        return;
      }
    }
    if (customMatchesPractice(vitalKind, baseline, periods, weightTargets)) {
      setUnchangedPromptOpen(true);
      return;
    }
    setConfirmCustomOpen(true);
  };

  const confirmCustomSave = () => {
    setConfirmCustomOpen(false);
    onConfirm({
      mode: 'custom',
      payload: buildCustomPayload(vitalKind, baseline, { daily, periods, weightTargets })
    });
  };

  return createPortal(
    <div className="bp-enroll-overlay" role="presentation">
      <div
        className="bp-enroll-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bp-enroll-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="bp-enroll-header">
          <h3 id="bp-enroll-title">
            {editingCustom ? `Custom ${vitalNoun} settings` : `Practice ${vitalNoun} settings`}
          </h3>
          <button type="button" className="bp-enroll-close" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>

        <div className="bp-enroll-body">
          {loading ? (
            <p className="bp-enroll-lead">Loading practice {vitalNoun} settings…</p>
          ) : (
            <>
              <p className="bp-enroll-lead">
                {editingCustom
                  ? `Edit the ${vitalNoun} targets for this patient. You can switch back to the practice settings without saving.`
                  : `These are the practice ${vitalNoun} targets. Use them for this patient, or edit custom settings.`}
              </p>
              {formError ? <p className="bp-enroll-error">{formError}</p> : null}

              <section className="bp-enroll-section">
                <h4>
                  {vitalKind === 'weight'
                    ? (editingCustom ? 'Weight schedule' : 'Practice weight schedule')
                    : vitalKind === 'bg'
                      ? (editingCustom ? 'Blood glucose targets' : 'Practice blood glucose targets')
                      : (editingCustom ? 'Blood pressure targets' : 'Practice blood pressure targets')}
                </h4>
                <div className="bp-period-list">
                  {previewPeriods.map((row, index) => (
                    <article key={row.name} className="bp-period-card">
                      <h5>{String(row.name).replace(/\s*Time$/i, '')}</h5>
                      <div className={`bp-period-grid${vitalKind === 'bg' ? ' is-two' : vitalKind === 'weight' ? ' is-one' : ''}`}>
                        <div className="bp-field">
                          <span className="bp-field-label">Window</span>
                          <div className="bp-pair">
                            <input
                              type="text"
                              readOnly
                              aria-label={`${row.name} window start`}
                              value={to12Hour(row.start) || row.start}
                            />
                            <span className="bp-pair-sep">to</span>
                            <AmPmTimeField
                              ariaLabel={`${row.name} window end`}
                              value={row.end || ''}
                              disabled={!editable}
                              onChange={(time) => updatePeriod(index, 'end', time)}
                            />
                          </div>
                        </div>
                        {vitalKind === 'bp' ? (
                          [
                            ['sysMin', 'sysMax', 'Systolic'],
                            ['diaMin', 'diaMax', 'Diastolic'],
                            ['pulseMin', 'pulseMax', 'Pulse']
                          ].map(([minKey, maxKey, label]) => (
                            <div key={label} className="bp-field">
                              <span className="bp-field-label">{label}</span>
                              <div className="bp-pair">
                                <input
                                  type="number"
                                  min="0"
                                  max="300"
                                  aria-label={`${row.name} ${label} minimum`}
                                  value={row[minKey]}
                                  disabled={!editable}
                                  onChange={(event) => updatePeriod(index, minKey, event.target.value)}
                                />
                                <span className="bp-pair-sep">to</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="300"
                                  aria-label={`${row.name} ${label} maximum`}
                                  value={row[maxKey]}
                                  disabled={!editable}
                                  onChange={(event) => updatePeriod(index, maxKey, event.target.value)}
                                />
                              </div>
                            </div>
                          ))
                        ) : null}
                        {vitalKind === 'bg' ? (
                          <div className="bp-field">
                            <span className="bp-field-label">Glucose</span>
                            <div className="bp-pair">
                              <input
                                type="number"
                                min="0"
                                max="600"
                                aria-label={`${row.name} glucose minimum`}
                                value={row.min}
                                disabled={!editable}
                                onChange={(event) => updatePeriod(index, 'min', event.target.value)}
                              />
                              <span className="bp-pair-sep">to</span>
                              <input
                                type="number"
                                min="0"
                                max="600"
                                aria-label={`${row.name} glucose maximum`}
                                value={row.max}
                                disabled={!editable}
                                onChange={(event) => updatePeriod(index, 'max', event.target.value)}
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
              {vitalKind === 'weight' ? (
                <section className="bp-enroll-section">
                  <h4>{editingCustom ? 'Weight targets' : 'Practice weight targets'}</h4>
                  <div className="bp-period-list">
                    <article className="bp-period-card">
                      <div className="bp-period-grid is-two">
                        {[
                          ['weightMin', 'weightMax', 'Weight'],
                          ['bodyFatMin', 'bodyFatMax', 'Body fat']
                        ].map(([minKey, maxKey, label]) => (
                          <div key={label} className="bp-field">
                            <span className="bp-field-label">{label}</span>
                            <div className="bp-pair">
                              <input
                                type="number"
                                min="0"
                                max="500"
                                aria-label={`${label} minimum`}
                                value={previewWeight[minKey]}
                                disabled={!editable}
                                onChange={(event) => updateWeight(minKey, event.target.value)}
                              />
                              <span className="bp-pair-sep">to</span>
                              <input
                                type="number"
                                min="0"
                                max="500"
                                aria-label={`${label} maximum`}
                                value={previewWeight[maxKey]}
                                disabled={!editable}
                                onChange={(event) => updateWeight(maxKey, event.target.value)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </article>
                  </div>
                </section>
              ) : null}
            </>
          )}
        </div>

        <div className="bp-enroll-footer">
          <button type="button" className="bp-enroll-cancel" onClick={onCancel}>
            Cancel
          </button>
          {editingCustom ? (
            <button type="button" className="bp-enroll-edit" onClick={viewPracticeSettings} disabled={loading}>
              View practice settings
            </button>
          ) : (
            <button type="button" className="bp-enroll-edit" onClick={startCustomEdit} disabled={loading}>
              Edit custom settings
            </button>
          )}
          <button
            type="button"
            className="bp-enroll-continue"
            onClick={editingCustom ? handleContinue : confirmPractice}
            disabled={loading}
          >
            {editingCustom ? 'Save custom settings' : 'Use practice settings'}
          </button>
        </div>
        {confirmCustomOpen ? (
          <div className="bp-enroll-confirm" role="presentation">
            <div
              className="bp-enroll-confirm-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bp-enroll-confirm-title"
            >
              <h3 id="bp-enroll-confirm-title">Add custom settings</h3>
              <p>Are you sure you want to add the custom settings?</p>
              <div className="bp-enroll-confirm-actions">
                <button type="button" className="bp-enroll-cancel" onClick={() => setConfirmCustomOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="bp-enroll-continue" onClick={confirmCustomSave}>
                  Yes, add custom settings
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {unchangedPromptOpen ? (
          <div className="bp-enroll-confirm" role="presentation">
            <div
              className="bp-enroll-confirm-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bp-enroll-unchanged-title"
            >
              <h3 id="bp-enroll-unchanged-title">No changes made</h3>
              <p>Nothing has been changed in the custom settings. Change a target before saving, or use the practice settings.</p>
              <div className="bp-enroll-confirm-actions">
                <button type="button" className="bp-enroll-cancel" onClick={() => setUnchangedPromptOpen(false)}>
                  Keep editing
                </button>
                <button
                  type="button"
                  className="bp-enroll-continue"
                  onClick={() => {
                    setUnchangedPromptOpen(false);
                    confirmPractice();
                  }}
                >
                  Use practice settings
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default BpEnrollmentSettingsModal;
